/**
 * API Client Configuration
 *
 * Axios instance with interceptors for authentication and error handling
 */
import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Create axios instance
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // 陣列參數不使用索引，產生 key=value1&key=value2 格式
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper function to ensure JSON fields are arrays
const ensureArrayFields = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(ensureArrayFields);
  }

  // Create a new object to avoid mutating the original
  const result: any = {};

  for (const key in obj) {
    const value = obj[key];

    // Known JSON array fields that might come as strings from MySQL
    if (['use_type', 'parameters_input', 'methodologies'].includes(key)) {
      // If it's a string that looks like JSON, parse it
      if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
        try {
          result[key] = JSON.parse(value);
        } catch {
          result[key] = [];
        }
      } else if (value === null || value === undefined) {
        result[key] = [];
      } else if (!Array.isArray(value)) {
        // If it's not an array but should be, wrap it
        result[key] = [value];
      } else {
        result[key] = value;
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = ensureArrayFields(value);
    } else {
      result[key] = value;
    }
  }

  return result;
};

// Response interceptor - Handle errors and fix JSON fields
apiClient.interceptors.response.use(
  (response) => {
    // Skip transformation for Blob responses (file downloads/previews)
    if (response.data instanceof Blob) {
      console.log('[API Client] Blob response detected, skipping transformation');
      return response;
    }

    // Log raw response data for debugging
    if (response.config.url?.includes('/projects/')) {
      console.log('[API Client] Raw response from:', response.config.url);
      console.log('[API Client] Raw data before transformation:', JSON.parse(JSON.stringify(response.data)));
    }

    // Fix JSON array fields that might come as strings
    if (response.data) {
      response.data = ensureArrayFields(response.data);

      // Log transformed data
      if (response.config.url?.includes('/projects/')) {
        console.log('[API Client] Data after ensureArrayFields:', response.data);
        if (response.data.methodologies) {
          console.log('[API Client] methodologies field type:', typeof response.data.methodologies);
          console.log('[API Client] methodologies is Array:', Array.isArray(response.data.methodologies));
          console.log('[API Client] methodologies value:', response.data.methodologies);
        }
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth-storage');
          window.location.href = '/auth/login';
          break;
        case 403:
          // Forbidden - show permission error
          console.error('Access forbidden:', error.response.data);
          break;
        case 404:
          // 404 is often expected (e.g., resource not created yet)
          // Don't log to console to avoid confusion
          break;
        case 500:
          console.error('Server error:', error.response.data);
          break;
        default:
          console.error('API Error:', error.response.data);
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.message);
    } else {
      // Other errors
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
